import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import MediaGallery from '../../MediaGallery/MediaGallery';
import {AssistantHeader} from '../AssistantHeader';
import {AssistantFooter} from '../AssistantFooter';
import {AssistantContent} from '../AssistantContent';
import {AssistantSubHeader} from '../AssistantSubHeader';
import {AssistantNav} from '../AssistantNav';
import {useAssistant} from '../../Context/Assistant/AssistantContext';
import {AssistantActions} from '../../Context/Assistant/AssistantActionTypes';
import {useTranslation} from 'react-i18next';
import AssistantStepTip from '../Common/AssistantStepTip/AssistantStepTip';
import {CoverTextSubStep} from './SubStep/CoverTextSubStep';
import {useExperience} from '../../Experience/ExperienceProvider';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {AssistantAssetsProps, AssistantCommonProps, AssistantNavigationProps} from '../Assistant';

export enum CoverMode {
  Front = 'front',
  Back = 'back',
}

export enum CoverTextSubStepNegativeAction {
  Close,
  Back,
}

type CreateCoverStepProps = {
  coverMode: CoverMode;
  canShowTextDefaultMode: boolean;
} & AssistantCommonProps &
  AssistantAssetsProps &
  AssistantNavigationProps;

export const CreateCoverStep = ({
  assets,
  coverMode,
  backButton,
  mediaGalleryProps,
  displayMode,
  onClose,
  canShowTextDefaultMode,
  ...props
}: CreateCoverStepProps) => {
  const assistant = useAssistant();
  const ui = useUiState();
  const {experience} = useExperience();
  const {t} = useTranslation('assistants');

  const [helpTipOpen, setHelpTipOpen] = useState<boolean>(false);

  const coverItems = assistant.state.coverItems[coverMode];
  const [textOptionsOpen, setTextOptionsOpen] = useState<boolean>(false);
  const timeoutList = useRef<number[]>([]);

  const {
    state: {settings},
  } = assistant;
  const coverStep = settings[CoverMode.Back === coverMode ? 'cover2Step' : 'cover1Step'];
  const closeTextSubStepAction =
    0 === coverStep.pictures.length && Object.keys(coverStep.text).length > 0
      ? CoverTextSubStepNegativeAction.Back
      : CoverTextSubStepNegativeAction.Close;

  useEffect(() => {
    timeoutList.current.push(
      window.setTimeout(() => {
        setHelpTipOpen(true);
      }, 500)
    );

    return () => {
      if (timeoutList.current.length > 0)
        timeoutList.current.forEach(timeout => window.clearTimeout(timeout));
    };
  }, []);

  useLayoutEffect(() => {
    // If the cover doesn't have pictures, but has text, then open the text sub step immediately.
    if (
      0 === coverStep.pictures.length &&
      Object.keys(coverStep.text).length > 0 &&
      canShowTextDefaultMode
    ) {
      // Needs to be in a timeout, otherwise it renders behind the assistant.
      timeoutList.current.push(
        window.setTimeout(() => {
          setTextOptionsOpen(true);
        }, 50)
      );
    }
  }, [coverMode, closeTextSubStepAction, canShowTextDefaultMode]);

  const getNextButton = (): React.ReactElement => {
    return React.cloneElement(props.nextButton, {
      ...props.nextButton.props,
      onClick: () => {
        const allowedByExperience =
          CoverMode.Back === coverMode
            ? experience.WIZARD.backCoverStep.allowCustomersToSelectPicturesAndText
            : experience.WIZARD.cover1Step.allowCustomersToSelectPicturesAndText;

        if (allowedByExperience && assistant.state.coverItems[coverMode].textBox.length > 0) {
          setTextOptionsOpen(true);
        } else {
          props.nextButton.props.onClick();
        }
      },
    });
  };

  const getTipTitle = () => {
    return CoverMode.Back === coverMode ? t('*:str_LabelBackCover') : t('*:str_LabelFrontCover');
  };

  const getTipMessage = () => {
    let tipString = 'str_MessageCoverTipSingle';
    const maxCoverImages = coverItems.pictureBox.length;
    if (maxCoverImages > 1) {
      tipString = 'str_MessageCoverStepTipMultiple';
    }

    return t(tipString).replace('^0', String(maxCoverImages));
  };

  const helpTip = (
    <AssistantStepTip
      title={getTipTitle()}
      message={getTipMessage()}
      open={helpTipOpen}
      onClose={() => setHelpTipOpen(false)}
      key={`cover-helptip-${coverMode}`}
      nextFocusContainerId={'assistant-content'}
    />
  );

  const nextButton = getNextButton();

  return (
    <>
      <AssistantHeader displayMode={displayMode} />
      <AssistantNav
        onClose={onClose}
        backButton={backButton}
        nextButton={nextButton}
        onNext={nextButton.props.onClick}
      />
      <AssistantSubHeader
        assetCount={assets.length}
        minSelection={coverItems.pictureBox.length}
        coverAssetsCount={assistant.state.coverAssets[coverMode].length}
        maxCoverAssetsCount={assistant.state.coverItems[coverMode].pictureBox.length}
        showAddPicturesButton={false}
      ></AssistantSubHeader>
      <AssistantContent className={'lg:p-lg sm:p-sm'}>
        <MediaGallery
          {...mediaGalleryProps}
          maxSelection={coverItems.pictureBox.length}
          onSelectPicture={(checked, asset) => {
            if (
              1 === coverItems.pictureBox.length &&
              !assistant.state.coverAssets[coverMode].includes(asset.id)
            ) {
              assistant.dispatch(
                AssistantActions.setCoverAssets({coverType: coverMode, assets: [asset.id]})
              );
              return;
            } else if (
              assistant.state.coverAssets[coverMode].length === coverItems.pictureBox.length &&
              !assistant.state.coverAssets[coverMode].includes(asset.id) &&
              checked
            ) {
              // prevent further selections if limit has been reached,
              // unless the asset is already selected, then we allow it to be deselected.
              return;
            }

            if (checked) {
              assistant.dispatch(
                AssistantActions.addCoverAssets({coverType: coverMode, assets: [asset.id]})
              );
            } else {
              assistant.dispatch(
                AssistantActions.removeCoverAssets({coverType: coverMode, assets: [asset.id]})
              );
            }
          }}
          selectedImages={assistant.state.coverAssets[coverMode]}
          key={`assistant-coverstep-${coverMode}`}
          onItemDragStart={undefined}
          onItemTouchStart={undefined}
        />
        {coverItems.pictureBox.length > 0 && helpTip}
        <CoverTextSubStep
          key={`cover-text-options-${coverMode}`}
          open={textOptionsOpen}
          coverMode={coverMode}
          height={ui.state.isSmallScreen ? 660 : undefined}
          width={ui.state.isSmallScreen ? 400 : 660}
          negativeAction={closeTextSubStepAction}
          onClose={() => {
            setTextOptionsOpen(false);

            if (CoverTextSubStepNegativeAction.Back === closeTextSubStepAction) {
              backButton.props.onClick();
            }
          }}
          onAfterClose={() => {}}
          onContinue={() => {
            setTextOptionsOpen(false);
            props.nextButton.props.onClick();
          }}
        />
      </AssistantContent>
      <AssistantFooter />
    </>
  );
};
